/**
 * DESIGN SYSTEM NOTE: Hard-coded Tailwind colors in this file (bg-[#41788E], etc.)
 * are intentional and match approved Figma designs. Platform CSS variables will be updated
 * to match Figma in a future initiative, then this file will be refactored to use semantic tokens.
 * See PR #9637 for context on the Figma-first strategy.
 */

import { observer } from 'mobx-react';
import { useContext } from 'react';
import { Maybe } from 'maybeasy';
import { MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { T, TranslationsContext, translation } from '../../../../../../../../Translations';
import { ClientExperienceResource, ProductCollectionResource } from '../../../Types';
import EnhancedCourseCard from './EnhancedCourseCard';
import QuickAddCourses from './QuickAddCourses';
import { sortingOptions } from '../../../../Builder/ExperienceCatalog/SortTemplate/Types';
import { trackIntercomEvent } from '../../../../../../../../services/intercomTrackingService';

interface Props {
  experiences: ClientExperienceResource[];
  selectedCourseId: Maybe<number>;
  onSelectCourse: (courseId: number) => void;
  productCollection: ProductCollectionResource;
  index: number;
  courseRefs: React.MutableRefObject<{ [key: number]: HTMLDivElement | null }>;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onFiltersClick: () => void;
  activeFiltersCount: number;
  currentSortValue: string;
  onSortChange: (value: string) => void;
  addedCourseIds: number[];
  onToggleCourse: (courseId: number) => void;
  onAddCourses: (courseIds: number[]) => void;
  onRemoveCourses: (courseIds: number[]) => void;
}

function CourseListPanel({
  experiences,
  selectedCourseId,
  onSelectCourse,
  productCollection,
  index,
  courseRefs,
  searchQuery,
  onSearchChange,
  onFiltersClick,
  activeFiltersCount,
  currentSortValue,
  onSortChange,
  addedCourseIds,
  onToggleCourse,
  onAddCourses,
  onRemoveCourses,
}: Props) {
  const ts = useContext(TranslationsContext);
  const handleSearchChange = (query: string) => {
    if (query) {
      trackIntercomEvent('Builder V2 - Course Catalog - Search Courses', {
        searchQuery: query,
      });
    }
    onSearchChange(query);
  };

  const handleFiltersClick = () => {
    trackIntercomEvent('Builder V2 - Course Catalog - Open Filters');
    onFiltersClick();
  };

  const handleSortChange = (value: string) => {
    const selectedOption = sortingOptions.find((opt) => opt.value === value);
    trackIntercomEvent('Builder V2 - Course Catalog - Change Sort', {
      sortValue: value,
      sortLabel: selectedOption?.label || value,
    });
    onSortChange(value);
  };

  return (
    <div
      className="w-2/5 border-r border-border flex flex-col bg-card"
      data-intercom-target="builder-v2-course-list-panel"
    >
      {/* Search and Filter Bar */}
      <div className="flex-none px-4 pt-4 pb-3 border-b border-border space-y-3">
        {/* Search Bar */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder={translation('Search courses, schools, competencies...')(ts)}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            style={{ fontSize: 'var(--text-sm)' }}
            data-intercom-target="builder-v2-course-search"
          />
        </div>

        {/* Filter Button, Sort, and Results Count */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleFiltersClick}
              className="flex items-center gap-2 px-4 py-2 border border-border rounded-md bg-background hover:bg-muted transition-colors"
              style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
              data-intercom-target="builder-v2-filter-button"
            >
              <FunnelIcon className="w-4 h-4" />
              <span>
                <T kind="Filters" />
              </span>
              {activeFiltersCount > 0 && (
                <span
                  className="px-2 py-0.5 bg-[#41788E] text-white rounded-full"
                  style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)' }}
                >
                  {activeFiltersCount}
                </span>
              )}
            </button>

            <select
              value={currentSortValue}
              onChange={(e) => handleSortChange(e.target.value)}
              className="pl-4 pr-8 py-2 border border-border rounded-md bg-background hover:bg-muted transition-colors cursor-pointer"
              style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
              data-intercom-target="builder-v2-sort-select"
            >
              {sortingOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {`Sort: ${option.label}`}
                </option>
              ))}
            </select>
          </div>

          <div className="inline-flex items-center px-3 py-1 bg-[#41788E] text-white rounded-full">
            <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}>
              {experiences.length} course{experiences.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Quick Add: bulk add/remove by course type, following the current search and filters */}
        <QuickAddCourses
          experiences={experiences}
          addedCourseIds={addedCourseIds}
          onAddCourses={onAddCourses}
          onRemoveCourses={onRemoveCourses}
        />
      </div>

      {/* Scrollable Course List */}
      <div className="flex-1 overflow-y-auto p-4">
        {experiences.map((experience, idx) => {
          const isSelected = selectedCourseId
            .map((id) => id === experience.payload.id)
            .getOrElseValue(false);
          const isLast = idx === experiences.length - 1;

          return (
            <div
              key={experience.payload.id}
              ref={(el) => {
                courseRefs.current[experience.payload.id] = el;
              }}
            >
              <EnhancedCourseCard
                experience={experience}
                isSelected={isSelected}
                onSelect={() => onSelectCourse(experience.payload.id)}
                productCollection={productCollection}
                index={index}
                isAdded={addedCourseIds.includes(experience.payload.id)}
                onToggle={() => onToggleCourse(experience.payload.id)}
              />
              {!isLast && <div className="h-px bg-border my-2" />}
            </div>
          );
        })}

        {/* Empty State */}
        {experiences.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-muted-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <h4 className="mb-2">
              <T kind="No courses found" />
            </h4>
            <p className="text-muted-foreground" style={{ fontSize: 'var(--text-sm)' }}>
              <T kind="Try adjusting your search or filters" />
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default observer(CourseListPanel);
